import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  attachDeviceToBuilding,
  getDevicesNotAssociatedToBuilding,
} from "../../../api/device";
import {
  Building,
  DeviceWithoutLegacyPassword,
} from "../../../generated/types";
import { I18nKey } from "../../../i18n/i18n";
import { getDeviceDisplayName } from "../../../utils/breedingDevices";
import { Button } from "../../common/Button";
import { FormSelect } from "../../common/FormLayout";
import { BUILDING_EXCLUDED_TYPES } from "../setup/AssociateDeviceForm";
import { DeviceTypeBadge } from "./DeviceTypeBadge";

/**
 * Produits déjà à l'entreprise mais dans aucun bâtiment (une Serenity, ou le produit dont
 * le ticket a ouvert le modal) : on les rattache à l'un des bâtiments proposés, sans
 * repasser par l'association. Avec un seul bâtiment, pas de choix à faire.
 */
export const UnattachedDevices = ({
  companyId,
  buildings,
}: {
  companyId: number;
  buildings: Building[];
}) => {
  const { t } = useTranslation();
  const [devices, setDevices] = useState<DeviceWithoutLegacyPassword[]>([]);
  const [choices, setChoices] = useState<Record<number, string>>({});
  const [attached, setAttached] = useState<number[]>([]);
  const [error, setError] = useState<I18nKey | "">("");
  const singleBuilding = buildings.length === 1 ? String(buildings[0].id) : "";

  useEffect(() => {
    let cancelled = false;
    getDevicesNotAssociatedToBuilding(companyId).then((result) => {
      if (cancelled) return;
      setDevices(
        result.devices.filter(
          (device) => !BUILDING_EXCLUDED_TYPES.includes(device.type)
        )
      );
    });
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  const buildingOf = (deviceId: number) =>
    singleBuilding || choices[deviceId] || "";

  const attach = async (deviceId: number) => {
    setError("");
    const result = await attachDeviceToBuilding(
      deviceId,
      Number(buildingOf(deviceId))
    );
    if (result) {
      setError(result);
      return;
    }
    setAttached((prev) => [...prev, deviceId]);
  };

  const remaining = devices.filter((device) => !attached.includes(device.id));
  if (remaining.length === 0) return null;

  return (
    <div className="space-y-3 border-b border-grey-light pb-4">
      <p className="font-oxaniumMedium text-default-text">
        {t("breeding-create-unattached-devices")}
      </p>
      {error && <p className="text-sm text-red-600">{t(error)}</p>}
      {remaining.map((device) => (
        <div key={device.id} className="flex flex-wrap items-end gap-3">
          <p className="flex items-center gap-2 text-default-text min-w-0 flex-1">
            <DeviceTypeBadge type={device.type} />
            <span className="truncate">{getDeviceDisplayName(device)}</span>
          </p>
          {!singleBuilding && (
            <div className="flex-1 min-w-[10rem]">
              <FormSelect
                label={t("device-attach-building-label")}
                value={choices[device.id] ?? ""}
                onChange={(e) =>
                  setChoices((prev) => ({
                    ...prev,
                    [device.id]: e.target.value,
                  }))
                }
                options={buildings.map((building) => ({
                  value: String(building.id),
                  label: building.name,
                }))}
              />
            </div>
          )}
          <Button
            type="button"
            variant="secondary"
            disabled={!buildingOf(device.id)}
            onClick={() => attach(device.id)}
          >
            {t("device-attach-building-submit")}
          </Button>
        </div>
      ))}
    </div>
  );
};
